<template>
  <li
    v-if="shouldRenderMessage"
    :id="`message${data.id}`"
    ref="message"
    :class="alignBubble"
    class="email--message"
  >
    <div :class="wrapClass">
      <div
        v-if="data.sender && !data.private"
        v-tooltip.top-start="messageToolTip"
        :class="bubbleClass"
        @contextmenu="openContextMenu($event)"
      >
        <div class="px-2 flex justify-between">
          <div class="flex items-start gap-2">
            <div v-tooltip.left="tooltipForSender" class="sender--info">
              <woot-thumbnail
                :src="sender.thumbnail"
                :username="sender.name"
                size="32px"
              />
            </div>
            <bubble-mail-head
              :email-attributes="contentAttributes.email"
              :cc="emailHeadAttributes.cc"
              :bcc="emailHeadAttributes.bcc"
              :fwd="emailHeadAttributes.fwd"
              :from="emailHeadAttributes.from"
              :is-incoming="isIncoming"
            />
          </div>
          <div class="flex flex-col">
            <div v-if="!isFailed" class="flex justify-between">
              <div class="email-action-buttons">
                <button
                  v-tooltip="$t('CONVERSATION.REPLYBOX.REPLY')"
                  class="cursor-pointer"
                  @click="handleReplyTo(replyMode)"
                >
                  <fluent-icon icon="arrow-reply" />
                </button>
                <button
                  v-tooltip="$t('CONVERSATION.REPLYBOX.REPLY_ALL')"
                  class="cursor-pointer"
                  @click="handleReplyTo(replyMode)"
                >
                  <fluent-icon icon="arrow-reply-all" />
                </button>
                <button
                  v-tooltip="$t('CONVERSATION.REPLYBOX.FORWARD')"
                  class="cursor-pointer"
                  @click="handleForwardTo"
                >
                  <fluent-icon icon="arrow-forward" />
                </button>
                <button
                  v-tooltip="$t('CONVERSATION.REPLYBOX.PRIVATE_NOTE')"
                  class="cursor-pointer"
                  :title="$t('CONVERSATION.REPLYBOX.PRIVATE_NOTE')"
                  @click="handleNote(privateMode)"
                >
                  <fluent-icon icon="pen" />
                </button>
                <div v-if="shouldShowContextMenu" class="context-menu-wrap">
                  <context-menu
                    v-if="isBubble && !isMessageDeleted"
                    :context-menu-position="contextMenuPosition"
                    :is-open="showContextMenu"
                    :d99d-enables="d99dEnables"
                    :enabled-options="contextMenuEnabledOptions"
                    :message="data"
                    :hide-translate-option="true"
                    @open="openContextMenu"
                    @close="closeContextMenu"
                    @remoteAssignFile="handleRemoteAssignFile"
                    @d99dSelect="handleD99dSelection"
                    @replyTo="handleReplyTo(replyMode)"
                  />
                </div>
              </div>
            </div>
            <bubble-actions
              :id="data.id"
              :sender="data.sender"
              :external-error="externalError"
              :is-a-tweet="isATweet"
              :is-a-whatsapp-channel="false"
              :has-instagram-story="false"
              :is-email="isEmailContentType"
              :is-private="data.private"
              :message-type="data.message_type"
              :message-status="status"
              :source-id="data.source_id"
              :inbox-id="data.inbox_id"
              :created-at="createdAt"
              :status-history="data.status_history"
              :delay-hours="data.delay_hours"
            />
          </div>
        </div>
        <bubble-text
          v-if="data.content"
          :message="message"
          :is-email="isEmailContentType"
          :display-quoted-button="displayQuotedButton"
        />
        <div v-if="showReplyButtons" class="flex">
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-reply"
            class="reply-button"
            @click="handleReplyTo(replyMode)"
          >
            {{ $t('CONVERSATION.REPLYBOX.REPLY') }}
          </woot-button>
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-reply"
            class="reply-button"
            @click="handleReplyTo(replyMode)"
          >
            {{ $t('CONVERSATION.REPLYBOX.REPLY_ALL') }}
          </woot-button>
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-forward"
            class="reply-button"
            @click="handleForwardTo"
          >
            {{ $t('CONVERSATION.REPLYBOX.FORWARD') }}
          </woot-button>
          <woot-button
            variant="clear"
            size="small"
            icon="arrow-forward"
            class="reply-button"
            @click="handleNote(privateMode)"
          >
            {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
          </woot-button>
        </div>
        <bubble-reply-to
          v-if="
            inReplyToMessageId &&
            inboxSupportsReplyTo &&
            !contentAttributes.reply_data /* d99d */
          "
          :message="inReplyTo"
          :message-type="data.message_type"
        />
        <span
          v-if="isPending && hasAttachments"
          class="chat-bubble has-attachment agent"
        >
          {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
        </span>
        <div v-if="!isPending && hasAttachments">
          <div v-for="attachment in attachments" :key="attachment.id">
            <bubble-image-audio-video
              v-if="isAttachmentImageVideoAudio(attachment.file_type)"
              :attachment="attachment"
              @error="onImageLoadError"
            />
            <bubble-file v-else :url="attachment.data_url" />
          </div>
        </div>
      </div>
      <div
        v-else
        v-tooltip.top-start="messageToolTip"
        :class="bubbleClass"
        @contextmenu="openContextMenu($event)"
      >
        <bubble-text
          v-if="data.content"
          :message="message"
          :is-email="isEmailContentType"
          :display-quoted-button="displayQuotedButton"
        />
        <div v-if="data.private" class="flex">
          <p class="text-xs text-slate-500 mr-2 mb-0">
            {{ `${$t('CONVERSATION.SENT_BY')} ${data.sender.name}` }}
          </p>
        </div>
        <span
          v-if="isPending && hasAttachments"
          class="chat-bubble has-attachment agent"
        >
          {{ $t('CONVERSATION.UPLOADING_ATTACHMENTS') }}
        </span>
        <div v-if="!isPending && hasAttachments">
          <div v-for="attachment in attachments" :key="attachment.id">
            <bubble-image-audio-video
              v-if="isAttachmentImageVideoAudio(attachment.file_type)"
              :attachment="attachment"
              @error="onImageLoadError"
            />
            <bubble-file v-else :url="attachment.data_url" />
          </div>
        </div>
        <bubble-actions
          :id="data.id"
          :sender="data.sender"
          :external-error="externalError"
          :is-a-tweet="isATweet"
          :is-a-whatsapp-channel="false"
          :has-instagram-story="false"
          :is-email="isEmailContentType"
          :is-private="data.private"
          :message-type="data.message_type"
          :message-status="status"
          :source-id="data.source_id"
          :inbox-id="data.inbox_id"
          :created-at="createdAt"
          :status-history="data.status_history"
          :delay-hours="data.delay_hours"
        />
      </div>
      <spinner v-if="isPending" size="tiny" />
    </div>
  </li>
</template>
<script>
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import timeMixin from '../../../mixins/time';
import BubbleReply from './bubble/Reply';
import BubbleActions from './bubble/Actions.vue';
import BubbleFile from './bubble/File.vue';
import BubbleImageAudioVideo from './bubble/ImageAudioVideo.vue';
import BubbleMailHead from './bubble/MailHead.vue';
import BubbleText from './bubble/Text.vue';
import BubbleReplyTo from './bubble/ReplyTo.vue';
import Spinner from 'shared/components/Spinner.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import alertMixin from 'shared/mixins/alertMixin';
import contentTypeMixin from 'shared/mixins/contentTypeMixin';
import { MESSAGE_TYPE, MESSAGE_STATUS } from 'shared/constants/messages';
import { generateBotMessageContent } from './helpers/botMessageContentHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import { REPLY_EDITOR_MODES } from '../WootWriter/constants';

export default {
  name: 'EmailMessage',
  components: {
    BubbleActions,
    BubbleFile,
    BubbleImageAudioVideo,
    BubbleMailHead,
    BubbleText,
    BubbleReply,
    BubbleReplyTo,
    ContextMenu,
    Spinner,
  },
  mixins: [
    alertMixin,
    timeMixin,
    messageFormatterMixin,
    contentTypeMixin,
    hasPermissionMixin,
  ],
  props: {
    data: {
      type: Object,
      required: true,
    },
    isATweet: {
      type: Boolean,
      default: false,
    },
    deleteable: {
      type: Boolean,
      default: false,
    },
    assignable: {
      type: Boolean,
      default: false,
    },
    d99dFeatures: {
      // d99d
      type: Object,
      default: () => ({}),
    },
    inboxSupportsReplyTo: {
      type: Boolean,
      default: false,
    },
    inReplyTo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showContextMenu: false,
      hasImageError: false,
      contextMenuPosition: {},
      showBackgroundHighlight: false,
      d99dEnables: { empty: true }, // d99d
      hasImgStoryError: false,
    };
  },
  computed: {
    showReplyButtons() {
      return this.isIncoming;
    },
    privateMode() {
      return REPLY_EDITOR_MODES.NOTE;
    },
    replyMode() {
      return REPLY_EDITOR_MODES.REPLY;
    },
    reaction() {
      // d99d
      return this.contentAttributes.reaction_icon;
    },
    attachments() {
      // Here it is used to get sender and created_at for each attachment
      return this.data?.attachments.map(attachment => ({
        ...attachment,
        sender: this.data.sender || {},
        created_at: this.data.created_at || '',
      }));
    },
    shouldRenderMessage() {
      return (
        this.hasAttachments ||
        this.data.content ||
        this.isEmailContentType ||
        this.isAnIntegrationMessage ||
        ['referral', 'buttons', 'contact'].includes(this.contentType)
      );
    },
    emailMessageContent() {
      const {
        html_content: { full: fullHTMLContent } = {},
        text_content: { full: fullTextContent } = {},
      } = this.contentAttributes.email || {};
      return fullHTMLContent || fullTextContent || '';
    },
    displayQuotedButton() {
      if (this.emailMessageContent.includes('<blockquote')) {
        return true;
      }

      if (!this.isIncoming) {
        return false;
      }

      return false;
    },
    message() {
      // If the message is an email, emailMessageContent would be present
      // In that case, we would use letter package to render the email
      if (this.emailMessageContent /* && this.isIncoming d99d commented */) {
        return this.emailMessageContent;
      }

      const botMessageContent = generateBotMessageContent(
        this.contentType,
        this.contentAttributes,
        {
          noResponseText: this.$t('CONVERSATION.NO_RESPONSE'),
          csat: {
            ratingTitle: this.$t('CONVERSATION.RATING_TITLE'),
            feedbackTitle: this.$t('CONVERSATION.FEEDBACK_TITLE'),
          },
        }
      );

      if (this.contentType === 'input_csat') {
        return this.$t('CONVERSATION.CSAT_REPLY_MESSAGE') + botMessageContent;
      }

      return (
        this.formatMessage(this.data.content, false, this.data.private, false) +
        botMessageContent
      );
    },
    inReplyToMessageId() {
      // Why not use the inReplyTo object directly?
      // Glad you asked! The inReplyTo object may or may not be available
      // depending on the current scroll position of the message list
      // since old messages are only loaded when the user scrolls up
      return this.data.content_attributes?.in_reply_to;
    },
    contextMenuEnabledOptions() {
      return {
        scopeTiktok: false,
        copy: this.hasText,
        delete:
          (this.hasText || this.hasAttachments) &&
          // d99d
          (this.deleteable || this.data.private) &&
          !this.data.content_attributes?.story_id &&
          this.hasInboxPermission('Inbox delete message'),
        cannedResponse:
          this.isOutgoing &&
          this.hasText &&
          this.hasCreatePermission('Canned Responses'),
        replyTo: !this.data.private && this.inboxSupportsReplyTo,
        remoteAssignFile: this.assignable && this.hasMediaAndPdfAttachment,
      };
    },
    contentAttributes() {
      return this.data.content_attributes || {};
    },
    externalError() {
      return this.contentAttributes.external_error || null;
    },
    sender() {
      return this.data.sender || {};
    },
    status() {
      return this.data.status;
    },
    contentType() {
      const {
        data: { content_type: contentType },
      } = this;
      return contentType;
    },
    twitterProfileLink() {
      const additionalAttributes = this.sender.additional_attributes || {};
      const { screen_name: screenName } = additionalAttributes;
      return `https://twitter.com/${screenName}`;
    },
    alignBubble() {
      const { message_type: messageType } = this.data;
      const isCentered = messageType === MESSAGE_TYPE.ACTIVITY;
      const isLeftAligned = messageType === MESSAGE_TYPE.INCOMING;
      const isRightAligned =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return {
        center: isCentered,
        left: isLeftAligned,
        right: isRightAligned,
        'has-context-menu': this.showContextMenu,
        // this handles the offset required to align the context menu button
        // extra alignment is required since a tweet message has a the user name and avatar below it
        'has-tweet-menu': this.isATweet,
        'has-bg': this.showBackgroundHighlight,
      };
    },
    createdAt() {
      return this.contentAttributes.external_created_at || this.data.created_at;
    },
    isBubble() {
      return [0, 1, 3].includes(this.data.message_type);
    },
    isIncoming() {
      return this.data.message_type === MESSAGE_TYPE.INCOMING;
    },
    isOutgoing() {
      return this.data.message_type === MESSAGE_TYPE.OUTGOING;
    },
    isTemplate() {
      return this.data.message_type === MESSAGE_TYPE.TEMPLATE;
    },
    isAnIntegrationMessage() {
      return this.contentType === 'integrations';
    },
    emailHeadAttributes() {
      return {
        email: this.contentAttributes.email,
        cc: this.contentAttributes.cc_emails,
        bcc: this.contentAttributes.bcc_emails,
        fwd: this.contentAttributes.forwarded
          ? this.contentAttributes.to_emails
          : undefined, // d99d
        from: this.contentAttributes.from_email || undefined, // d99d
      };
    },
    hasAttachments() {
      return !!(this.data.attachments && this.data.attachments.length > 0);
    },
    hasMediaAndPdfAttachment() {
      if (this.hasAttachments) {
        try {
          const { attachments = [{}] } = this.data;
          let type = attachments[0].file_type;
          if (type === 'file') {
            type = attachments[0].data_url.split('.').pop();
          }
          return ['image', 'video', 'audio', 'pdf'].includes(type);
        } catch (e) {
          return false;
        }
      }
    },
    isMessageDeleted() {
      return this.contentAttributes.deleted;
    },
    hasText() {
      return !!this.data.content;
    },
    tooltipForSender() {
      const name = this.senderNameForAvatar;
      const { message_type: messageType } = this.data;
      const showTooltip =
        messageType === MESSAGE_TYPE.OUTGOING ||
        messageType === MESSAGE_TYPE.TEMPLATE;
      return showTooltip
        ? {
            content: `${this.$t('CONVERSATION.SENT_BY')} ${name}`,
          }
        : false;
    },
    messageToolTip() {
      if (this.isMessageDeleted) {
        return false;
      }
      return false;
    },
    wrapClass() {
      return {
        wrap: this.isBubble,
        'activity-wrap': !this.isBubble,
        'is-pending': this.isPending,
        'is-email': this.isEmailContentType,
      };
    },
    bubbleClass() {
      return {
        bubble: this.isBubble,
        'is-desired': this.data.desired,
        'is-private': this.data.private,
        'is-image': this.hasMediaAttachment('image'),
        'is-video': this.hasMediaAttachment('video'),
        'is-text': this.hasText,
        'is-from-bot': this.isSentByBot,
        'is-email': this.isEmailContentType,
        'bubble--hidden': this.isHidden, // d99d
      };
    },
    isPending() {
      return this.data.status === MESSAGE_STATUS.PROGRESS;
    },
    isFailed() {
      return this.data.status === MESSAGE_STATUS.FAILED; // d99d - we they red x
    },
    isSentByBot() {
      if (this.isPending) return false;
      return !this.sender.type || this.sender.type === 'agent_bot';
    },
    shouldShowContextMenu() {
      return !(this.isFailed || this.isPending);
    },
    errorMessage() {
      const { meta } = this.data;
      return meta ? meta.error : '';
    },
    showAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        return true;
      }
      return this.isATweet && this.isIncoming && this.sender;
    },
    senderNameForAvatar() {
      if (this.isOutgoing || this.isTemplate) {
        const { name = this.$t('CONVERSATION.BOT') } = this.sender || {};
        return name;
      }
      return '';
    },
    // d99d
    isLiked() {
      return (
        this.d99dFeatures.fb2Featureable && this.contentAttributes.is_liked
      );
    },
    isHidden() {
      return (
        this.d99dFeatures.fb2Featureable && this.contentAttributes.is_hidden
      );
    },
    // .
  },
  watch: {
    data() {
      this.hasImageError = false;
      this.hasImgStoryError = false;
    },
    // d99d
    async showContextMenu(val) {
      if (val) {
        if (!this.d99dFeatures.fb2Featureable) return;
        const { inbox_id, source_id } = this.data;
        if (this.d99dEnables.empty) {
          this.d99dEnables = {
            showLike: !this.contentAttributes.is_liked,
            showUnlike: this.contentAttributes.is_liked,
            showHide: this.isIncoming && !this.contentAttributes.is_hidden,
            showUnhide: this.isIncoming && this.contentAttributes.is_hidden,
          };
        } else if (this.d99dEnables.loading !== false)
          this.d99dEnables = { loading: true }; // only after changes
        const result = await this.$store.dispatch('getCommentInfo', {
          inbox_id,
          source_id,
        });
        const {
          user_likes: isLiked,
          can_like: canLike,
          can_hide: canHide,
          is_hidden: isHidden,
          can_reply_privately: canReplyPrivately,
        } = result;
        this.d99dEnables = {
          loading: false,
          showLike: canLike && !isLiked,
          showUnlike: canLike && isLiked,
          showHide: canHide && !isHidden,
          showUnhide: canHide && isHidden,
          showPrivateReply: canReplyPrivately,
        };
      }
    },
    // .
  },
  mounted() {
    this.hasImageError = false;
    this.hasImgStoryError = false;
    bus.$on(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    // this.setupHighlightTimer(); // d99d commented out (we use it in other place)
    this.$nextTick(() => {
      const telLinks = this.$refs.message?.querySelectorAll('a[href^="tel:"]');
      const emailLinks =
        this.$refs.message?.querySelectorAll('a[href^="mailto:"]');
      telLinks?.forEach(link => {
        link.addEventListener('click', this.handleTelLinkClick);
      });
      emailLinks?.forEach(link => {
        link.addEventListener('click', this.handleEmailLinkClick);
      });
    });
  },
  beforeDestroy() {
    bus.$off(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL, this.closeContextMenu);
    clearTimeout(this.higlightTimeout);
  },
  methods: {
    handleTelLinkClick(event) {
      event.preventDefault();
      const phoneNumber = event.target.href.replace('tel:', '');
      const phoneNumberTxt = event.target.textContent;
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        phoneNumber,
        name: this.getNameForPhone(phoneNumberTxt),
      });
    },
    handleEmailLinkClick(event) {
      event.preventDefault();
      const email = event.target.href.replace('mailto:', '');
      bus.$emit(BUS_EVENTS.START_NEW_CONVERSATION, {
        email,
        name: this.getNameForPhone(email),
      });
    },
    getNameForPhone(phoneNumber = '') {
      const content = this.data.content.replace(/\\n/gm, '\n ');
      // Escape special regex characters in the phone number
      const escapedPhoneNumber = phoneNumber.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      // Regex to capture name with potential company details immediately before a phone number
      const regex = new RegExp(
        `([א-תA-Za-z]+(?:[א-תA-Za-z0-9]*\\s+)*)(?=${escapedPhoneNumber})`
      );
      let match = content.match(regex);
      if (match) {
        // Capture the most probable name part, trimming unnecessary spaces
        return match[1].trim();
      }
      return ''; // Fallback to empty if no name found
    },
    isAttachmentImageVideoAudio(fileType) {
      return ['image', 'audio', 'video'].includes(fileType);
    },
    hasMediaAttachment(type) {
      if (this.hasAttachments && this.data.attachments.length > 0) {
        const { attachments = [{}] } = this.data;
        const { file_type: fileType } = attachments[0];
        return fileType === type && !this.hasImageError;
      }
      if (this.storyReply) {
        return true;
      }
      return false;
    },
    // d99d
    handleRemoteAssignFile() {
      bus.$emit(BUS_EVENTS.SET_PLATFORM_ASSIGN_FILE, this.data);
    },
    // .
    async retrySendMessage() {
      await this.$store.dispatch('sendMessageWithData', this.data);
    },
    onImageLoadError() {
      this.hasImageError = true;
    },
    onStoryLoadError() {
      this.hasImgStoryError = true;
    },
    isRTL() {
      return this.$root.$i18n.locale === 'he';
    },
    openContextMenu(e) {
      const shouldSkipContextMenu =
        e.target?.classList.contains('skip-context-menu') ||
        e.target?.tagName.toLowerCase() === 'a';
      if (shouldSkipContextMenu || getSelection().toString()) {
        return;
      }

      e.preventDefault();
      if (e.type === 'contextmenu') {
        this.$track(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
      }

      this.contextMenuPosition = {
        x: this.isRTL() ? e.pageX || e.clientX : (e.pageX || e.clientX) - 100,
        y: e.pageY || e.clientY,
      };

      this.showContextMenu = true;
    },
    closeContextMenu() {
      this.showContextMenu = false;
      this.contextMenuPosition = { x: null, y: null };
    },
    handleForwardTo() {
      this.$emit('forward', this.data);
    },
    handleNote(mode = REPLY_EDITOR_MODES.NOTE) {
      this.$emit('privateNote');
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_FORWARD_TO;
      const { conversation_id: conversationId, id: replyTo } = this.data;

      LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
      LocalStorage.updateJsonStore(replyStorageKey, 'mode', mode);
      bus.$emit(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.data);
    },
    handleReplyTo(mode = REPLY_EDITOR_MODES.NOTE) {
      this.$emit('reply');
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const { conversation_id: conversationId, id: replyTo } = this.data;

      LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
      if ([REPLY_EDITOR_MODES.NOTE, REPLY_EDITOR_MODES.REPLY].includes(mode)) {
        LocalStorage.updateJsonStore(replyStorageKey, 'mode', mode);
      }
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.data);
    },
    setupHighlightTimer(messageId) {
      if (Number(messageId) !== Number(this.data.id)) {
        // d99d replaced this.$route.query.messageId
        return;
      }

      this.showBackgroundHighlight = true;
      const HIGHLIGHT_TIMER = 1000;
      this.higlightTimeout = setTimeout(() => {
        this.showBackgroundHighlight = false;
      }, HIGHLIGHT_TIMER);
    },
    // d99d
    async handleD99dSelection(option) {
      // this.showContextMenu = false;
      const { inbox_id, source_id } = this.data;
      const payload = { inbox_id, source_id };
      this.d99dEnables = {};
      try {
        switch (option) {
          case 'like':
            await this.$store.dispatch('createLike', payload);
            break;
          case 'unlike':
            await this.$store.dispatch('deleteLike', payload);
            break;
          case 'hide':
            await this.$store.dispatch('hide', payload);
            break;
          case 'unhide':
            await this.$store.dispatch('unhide', payload);
            break;
          case 'tiktokUnlike':
            payload.liked = false;
            await this.$store.dispatch('sendTiktokLike', payload);
            break;
          case 'tiktokLike':
            payload.liked = true;
            await this.$store.dispatch('sendTiktokLike', payload);
            break;
          case 'tiktokUnhide':
            payload.hide = false;
            await this.$store.dispatch('sendTiktokHide', payload);
            break;
          case 'tiktokHide':
            payload.hide = true;
            await this.$store.dispatch('sendTiktokHide', payload);
            break;
          case 'private_reply':
            this.d99dFeatures.handlePrivateReply(this.data);
            return;
          default:
            return;
        }

        this.showAlert(this.$t('CONVERSATION.SUCCESS_FB_COMMENT_UPDATE'));
      } catch (error) {
        this.showAlert(this.$t('CONVERSATION.FAIL_FB_COMMENT_UPDATE'));
      }
    },
    // .
  },
};
</script>
<style lang="scss" scoped>
.reply-button {
  @apply border-solid;
  margin: 0px;
  margin-right: 2px;
  border-color: #E0E0E0 !important;
  color: black !important;
  align-items: center;
  box-sizing: border-box;
  display: inline-flex;
  border-radius: 4px;
  color: unset;
  margin-left: 4px;
  justify-content: center;
  text-decoration-line: none;
  vertical-align: middle;
  overflow: hidden;

  outline-style: none;
  padding: 5px 12px;
  min-width: 96px;
}
.email--message {
  .activity-wrap {
    width: 100%;
    margin: 0 1rem;
  }
  .wrap,
  .bubble {
    width: 100%;
    max-width: 100%;
    @apply mx-0;
    @apply border-b border-solid border-slate-700;
  }
  .bubble.is-text,
  .bubble {
    @apply p-1;
    @apply text-black-800 relative border-0 bg-white rounded-none border-slate-700;
  }
  ::v-deep {
    .reply-body,
    .reply-author {
      @apply text-black-800 relative border-b border-solid bg-white  border-slate-800;
    }
    .vert-line {
      @apply bg-slate-600;
    }
  }
}
.is-email {
  padding: 0 12px 12px;
}
.is-image {
  .message-text--metadata {
    position: relative;
    width: 100%;
    padding-top: 1rem;
  }
}
.context-menu {
  color: black;
}

.email-action-buttons {
  @apply flex flex-row justify-start gap-1;
}

[dir='rtl'] .email-action-buttons {
  @apply flex flex-row-reverse justify-start gap-1;
}

::v-deep {
  [id*='mail-editor-reference-message-container'] {
    display: none !important;
  }

  [id*='divRplyFwdMsg'] {
    display: none !important;
  }


  div[style*='border-top: solid #e1e1e1'],
  div[style*='border-top: solid #b5c4df'],
  div[style*='border-top:solid #e1e1e1'],
  div[style*='border-top:solid #b5c4df'],
  div[style*='border-top: 1pt solid'],
  div[style*='border-top:1pt solid'] {
    &:has(b) {
      display: none !important;

      & ~ * {
        display: none !important;
      }
    }
  }

  .gmail_quote,
  [class*='gmail_quote'],
  div[class*='gmail_extra'],
  blockquote[class*='gmail_quote'] {
    display: none !important;
  }

  blockquote[type='cite'],
  blockquote[cite] {
    display: none !important;
  }

  [id*='yahoo_quoted'],
  blockquote[class*='yahoo_quoted'] {
    display: none !important;
  }

  .text-content blockquote:not([class*='user-quote']) {
    display: none !important;
  }
}
</style>
